import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";
import ValidationService from "@/services/ValidationService";

const maxSize5MB = (value) => !value || value.size <= 5 * 1024 * 1024;

const GRADE_FEE_MIN = 0;
const GRADE_FEE_MAX = 2147483647;

export default {
  name: "GradeFeeImport",

  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },

  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      disableImportBtn: false,
      disableConfirmBtn: false,
      disableInputFields: false,
      disableAcademicYear: false,
      submitted: false,
      templateSubmitted: false,
      importStep: "form",
      periodArr: [],
      template_academic_year_id: null,
      academic_year_id: null,
      file_name: "",
      selectedFile: null,
      invalidFileType: false,
      previewRows: [],
      previewTotal: 0,
    };
  },

  validations: {
    academic_year_id: { required },
    selectedFile: { required, maxSize5MB },
  },

  computed: {
    disabledTemplateBtn() {
      return this.disableInputFields;
    },

    selectedAcademicYearName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.academic_year_id)
      );

      return selected?.text || this.previewRows[0]?.academic_year_name || "";
    },

    isPreviewStep() {
      return this.importStep === "preview";
    },
  },

  methods: {
    getAcademicYear() {
      this.isLoading = true;
      axios.get("period/periodList")
        .then((res) => {
          this.periodArr = res.data.data || [];
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    chooseFile() {
      if (this.isPreviewStep) {
        return;
      }

      this.$refs.fileUploader.value = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;

      if (!file) {
        return;
      }

      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!["xls", "xlsx"].includes(extension)) {
        this.invalidFileType = true;
        return;
      }

      this.selectedFile = file;
      this.file_name = file.name;
    },

    getTemplateFileName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.template_academic_year_id)
      );
      const periodName = (selected?.text || "all").replace(/\s+/g, "_");
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      return `${periodName}_grade_fees_${year}_${month}_${day}.xlsx`;
    },

    downloadTemplate() {
      this.templateSubmitted = true;

      if (this.disabledTemplateBtn) {
        return;
      }

      const params = {};
      if (this.template_academic_year_id) {
        params.academic_year_id = this.template_academic_year_id;
      }

      this.isLoading = true;
      axios.get("gradeFee/excelTemplateDownload", {
        params,
        responseType: "blob",
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");

          link.href = window.URL.createObjectURL(blob);
          link.download = this.getTemplateFileName();
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          this.handleError(error, "Failed to download the grade fee template.");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImport() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid || this.invalidFileType) {
        return;
      }

      this.validateImport();
    },

    validateImport() {
      this.isLoading = true;
      this.disableImportBtn = true;
      this.disableInputFields = true;
      this.disableAcademicYear = true;

      const formData = new FormData();
      formData.append("academic_year_id", this.academic_year_id);
      formData.append("file", this.selectedFile);

      axios.post("gradeFee/excelImportValidate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          this.previewRows = this.clonePreviewRows(response.data.preview || []);
          this.previewTotal = response.data.total || this.previewRows.length;
          this.clearImportConfirmErrors();
          this.importStep = "preview";
        })
        .catch((error) => {
          this.handleImportValidationError(error);
          this.disableInputFields = false;
          this.disableImportBtn = false;
          this.disableAcademicYear = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImportSubmit() {
      this.clearImportConfirmErrors();

      if (!this.previewRows.length) {
        this.$notification.error("No preview data to import.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      const fieldErrors = this.validatePreviewBeforeConfirm();
      if (Object.keys(fieldErrors).length) {
        this.setConfirmFieldErrors(fieldErrors);
        return;
      }

      this.isLoading = true;
      this.disableConfirmBtn = true;

      axios.post("gradeFee/excelImportConfirm", {
        academic_year_id: this.academic_year_id,
        preview: this.buildConfirmPreviewPayload(),
      })
        .then((response) => {
          this.$notification.success(response.data.message || "Grade fees imported successfully.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.showList();
        })
        .catch((error) => {
          this.handleConfirmBackendError(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.disableConfirmBtn = false;
        });
    },

    clonePreviewRows(rows) {
      return (rows || []).map((row) => ({
        academic_year_name: row.academic_year_name || "",
        grade_name: String(row.grade_name || "").trim(),
        campus_grade_id: row.campus_grade_id,
        annual_fee: row.annual_fee ?? "",
        termly_fee: row.termly_fee ?? "",
        monthly_fee: row.monthly_fee ?? "",
      }));
    },

    buildConfirmPreviewPayload() {
      return this.previewRows.map((row) => ({
        grade_name: String(row.grade_name || "").trim(),
        campus_grade_id: row.campus_grade_id,
        annual_fee: this.normalizeFeeValue(row.annual_fee),
        termly_fee: this.normalizeFeeValue(row.termly_fee),
        monthly_fee: this.normalizeFeeValue(row.monthly_fee),
      }));
    },

    normalizeFeeValue(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      if (typeof value === "string") {
        const trimmed = value.replace(/,/g, "").trim();
        if (trimmed === "") {
          return null;
        }
        return trimmed;
      }

      return value;
    },

    validatePreviewBeforeConfirm() {
      const errors = {};
      const seenGradeNames = {};

      this.previewRows.forEach((row, index) => {
        const rowErrors = {};
        const gradeName = String(row.grade_name || "").trim();

        if (!gradeName) {
          rowErrors.grade_name = "Grade Name is required.";
        } else if (gradeName.length > 255) {
          rowErrors.grade_name = "Max 255 characters.";
        } else if (seenGradeNames[gradeName]) {
          rowErrors.grade_name = "Duplicate grade in preview.";
        } else {
          seenGradeNames[gradeName] = true;
        }

        ["annual_fee", "termly_fee", "monthly_fee"].forEach((key) => {
          const normalized = this.normalizeFeeValue(row[key]);

          if (normalized === null) {
            rowErrors[key] = "Fee is required.";
            return;
          }

          if (!/^\d+(\.\d+)?$/.test(String(normalized))) {
            rowErrors[key] = "Must be a non-negative number.";
            return;
          }

          const numericFee = Number(normalized);

          if (numericFee < GRADE_FEE_MIN) {
            rowErrors[key] = `Must be at least ${GRADE_FEE_MIN}.`;
            return;
          }

          if (numericFee > GRADE_FEE_MAX) {
            rowErrors[key] = "Exceeds maximum.";
          }
        });

        if (Object.keys(rowErrors).length) {
          errors[index] = rowErrors;
        }
      });

      return errors;
    },

    handleImportValidationError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data || {};
        const messages = data.errors?.length
          ? data.errors
          : [data.message || "Import validation failed."];

        window.alert(messages.join("\n"));
        return;
      }

      this.handleError(error);
    },

    handleConfirmBackendError(error) {
      if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
        return;
      }
      this.handleError(error);
    },

    getImportConfirmErrorOptions() {
      return {};
    },

    resetForm() {
      this.submitted = false;
      this.templateSubmitted = false;
      this.importStep = "form";
      this.disableInputFields = false;
      this.disableAcademicYear = false;
      this.disableImportBtn = false;
      this.disableConfirmBtn = false;
      this.previewRows = [];
      this.previewTotal = 0;
      this.clearImportConfirmErrors();
      this.template_academic_year_id = null;
      this.academic_year_id = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.$v.$reset();

      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
      this.getAcademicYear();
    },

    showList() {
      this.$router.push({ name: "gradeFeeList" });
    },

    formatFee(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }

      return Number(value).toLocaleString();
    },

    onPreviewFieldInput(rowIndex, fieldKey) {
      // Strip unwanted characters from fee fields: '-', '+', 'e', 'E'
      if (["annual_fee", "termly_fee", "monthly_fee"].includes(fieldKey)) {
        const current = this.previewRows[rowIndex]?.[fieldKey];
        if (typeof current === "string") {
          const sanitized = current.replace(/[-+eE]/g, "");
          if (sanitized !== current) {
            this.$set(this.previewRows[rowIndex], fieldKey, sanitized);
          }
        } else if (typeof current === "number" && !Number.isFinite(current)) {
          this.$set(this.previewRows[rowIndex], fieldKey, "");
        }
      }

      if (this.confirmFieldErrors?.[rowIndex]?.[fieldKey]) {
        this.$delete(this.confirmFieldErrors[rowIndex], fieldKey);
        if (!Object.keys(this.confirmFieldErrors[rowIndex]).length) {
          this.$delete(this.confirmFieldErrors, rowIndex);
        }
      }
    },

    handleError(error, fallbackMessage = "Something went wrong.") {
      if (error.response?.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
        return;
      }

      const message = error.response?.data?.message || fallbackMessage;
      console.error(error);
      this.$notification.error(message, {
        timer: 3,
        position: "bottomCenter",
      });
    },
  },

  mounted() {
    this.getAcademicYear();
  },
};
